import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const CATEGORIAS = ['Vendas','Ferramentas','Marketing','Administrativo','Impostos','Salários','Outros'];

function fmt(v) {
  return (Number(v)||0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
}

function agruparFluxoPorMes(lancamentos, contasFixas) {
  const mapa = {};
  lancamentos.forEach(l => {
    const dataRef = l.data_lancamento || l.data_esperada;
    if (!dataRef) return;
    const mes = dataRef.slice(0,7);
    if (!mapa[mes]) mapa[mes] = { mes, confirmado:0, pendente:0, saidas:0 };
    if (l.tipo==='entrada') {
      if (l.status==='Recebido') mapa[mes].confirmado += Number(l.valor);
      else mapa[mes].pendente += Number(l.valor);
    } else {
      mapa[mes].saidas += Number(l.valor);
    }
  });
  const totalFixasMes = contasFixas.filter(c=>c.ativa).reduce((a,c)=>a+Number(c.valor),0);
  const hoje = new Date();
  const mesAtual = hoje.toISOString().slice(0,7);
  for (let i=0;i<3;i++) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth()+i, 1);
    const m = d.toISOString().slice(0,7);
    if (!mapa[m]) mapa[m] = { mes:m, confirmado:0, pendente:0, saidas:0 };
    if (m>=mesAtual) mapa[m].saidas += totalFixasMes;
  }
  return Object.values(mapa).sort((a,b)=>a.mes.localeCompare(b.mes)).slice(-6);
}

const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, background:'#FAF8FF', color:'#1E0A3C', fontFamily:'Poppins,sans-serif', boxSizing:'border-box' };

export default function Financeiro() {
  const [lancamentos, setLancamentos] = useState([]);
  const [contasFixas, setContasFixas] = useState([]);
  const [carregando, setCarregando]   = useState(true);
  const [aba, setAba]                 = useState('lancamentos');
  const [projetos, setProjetos]       = useState(['Geral']);
  const [modalLanc, setModalLanc]     = useState(false);
  const [modalConta, setModalConta]   = useState(false);
  const [editandoLanc, setEditandoLanc]   = useState(null);
  const [editandoConta, setEditandoConta] = useState(null);
  const [filtroTipo, setFiltroTipo]       = useState('');
  const [filtroStatus, setFiltroStatus]   = useState('');
  const [filtroProj, setFiltroProj]       = useState('');
  const [filtroKpi, setFiltroKpi]         = useState(null); // 'entradas'|'saidas'|'pendente'|'atrasado'|null
  const [formLanc, setFormLanc]           = useState({ descricao:'', tipo:'entrada', valor:'', data_lancamento:'', data_esperada:'', categoria:'Vendas', projeto:'Geral', status:'Pendente', obs:'' });
  const [formConta, setFormConta]         = useState({ descricao:'', valor:'', vencimento:1, categoria:'Ferramentas', ativa:true });

  useEffect(() => { carregarDados(); }, []);

  async function carregarDados() {
    setCarregando(true);
    const [{ data: lanc }, { data: fixas }, { data: projs }, { data: cobs }, { data: casos }] = await Promise.all([
      supabase.from('financeiro').select('*').order('created_at', { ascending:false }),
      supabase.from('contas_fixas').select('*').order('descricao'),
      supabase.from('projetos').select('id, nome').order('nome'),
      supabase.from('cobrancas_casos').select('*').order('data_prevista'),
      supabase.from('casos').select('id, nome_cliente, projeto_id').order('created_at', { ascending:false }),
    ]);

    const projetoNome = (id) => (projs||[]).find(p=>p.id===id)?.nome || '—';
    const hojeStr = new Date().toISOString().split('T')[0];

    function diasAtraso(dataStr) {
      if (!dataStr) return 0;
      return Math.floor((new Date(hojeStr) - new Date(dataStr)) / 86400000);
    }

    // Remove do financeiro qualquer entrada que já existe como cobranca_caso (evita duplicatas)
    const descricoesCasos = new Set((cobs||[]).map(c => c.descricao));
    const lancManuais = (lanc || []).filter(l => !(l.tipo === 'entrada' && descricoesCasos.has(l.descricao)));

    // Todos as cobrancas_casos viram lançamentos — sem deduplicação por descrição
    const lancDeCasos = (cobs||[]).map(c => {
      const dias = diasAtraso(c.data_prevista);
      const atrasado = c.status !== 'Recebido' && dias > 0;
      return {
        id:              'cob_' + c.id,
        tipo:            'entrada',
        descricao:       c.descricao,
        valor:           c.valor_total * ((c.pct_nosso||30)/100),
        data_esperada:   c.data_prevista,
        data_lancamento: c.data_recebimento || null,
        categoria:       'Casos',
        projeto:         projetoNome(c.projeto_id),
        status:          c.status === 'Recebido' ? 'Recebido' : atrasado ? 'Atrasado' : 'Pendente',
        _de_caso:        true,
        _cob_id:         c.id,
        _dias_atraso:    atrasado ? dias : 0,
      };
    });

    // Contas fixas ativas viram um lançamento no mês atual (já como Pago, sem poluir outros meses)
    const hoje = new Date();
    const mesAtualStr = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}`;
    const lancDeFixas = (fixas||[]).filter(c => c.ativa).map(c => {
      const dia = String(Math.min(c.vencimento||1, 28)).padStart(2,'0');
      const dataStr = `${mesAtualStr}-${dia}`;
      return {
        id:              `fixa_${c.id}_${mesAtualStr}`,
        tipo:            'saida',
        descricao:       c.descricao,
        valor:           Number(c.valor),
        data_esperada:   dataStr,
        data_lancamento: null,
        categoria:       c.categoria || 'Ferramentas',
        projeto:         'Geral',
        status:          'Pendente',
        _fixa:           true,
        _fixa_id:        c.id,
      };
    });

    setLancamentos([...lancManuais, ...lancDeCasos, ...lancDeFixas]);
    setContasFixas(fixas || []);
    setProjetos(['Geral', ...(projs||[]).map(p=>p.nome)]);
    setCarregando(false);
  }

  const lancFiltrados = lancamentos.filter(l => {
    if (filtroKpi === 'entradas') return l.tipo==='entrada' && l.status==='Recebido';
    if (filtroKpi === 'saidas')   return l.tipo==='saida'   && l.status==='Pago';
    if (filtroKpi === 'pendente') return l.tipo==='entrada' && l.status==='Pendente';
    if (filtroKpi === 'atrasado') return l.status==='Atrasado';
    return (
      (!filtroTipo   || l.tipo===filtroTipo) &&
      (!filtroStatus || l.status===filtroStatus) &&
      (!filtroProj   || l.projeto===filtroProj)
    );
  });

  const totalEntradas = lancamentos.filter(l=>l.tipo==='entrada'&&l.status==='Recebido').reduce((s,l)=>s+Number(l.valor),0);
  const totalSaidas   = lancamentos.filter(l=>l.tipo==='saida'&&l.status==='Pago').reduce((s,l)=>s+Number(l.valor),0);
  const totalPendente = lancamentos.filter(l=>l.tipo==='entrada'&&l.status==='Pendente').reduce((s,l)=>s+Number(l.valor),0);
  const totalAtrasado = lancamentos.filter(l=>l.status==='Atrasado').reduce((s,l)=>s+Number(l.valor),0);
  const saldo         = totalEntradas - totalSaidas;
  const totalFixas    = contasFixas.filter(c=>c.ativa).reduce((a,c)=>a+Number(c.valor),0);

  function abrirNovoLanc() {
    setEditandoLanc(null);
    setFormLanc({ descricao:'', tipo:'entrada', valor:'', data_lancamento:'', data_esperada:'', categoria:'Vendas', projeto:'Geral', status:'Pendente', obs:'' });
    setModalLanc(true);
  }

  async function salvarLanc() {
    if (!formLanc.descricao || !formLanc.valor) return;
    const item = { ...formLanc, valor: Number(formLanc.valor) };
    if (editandoLanc) {
      const { data } = await supabase.from('financeiro').update(item).eq('id', editandoLanc.id).select().single();
      if (data) setLancamentos(lancamentos.map(l=>l.id===editandoLanc.id?data:l));
    } else {
      const { data } = await supabase.from('financeiro').insert([item]).select().single();
      if (data) setLancamentos([data, ...lancamentos]);
    }
    setModalLanc(false);
  }

  async function marcarRecebidoOuPago(l) {
    const novoStatus = l.tipo==='entrada' ? 'Recebido' : 'Pago';
    const hoje = new Date().toISOString().split('T')[0];

    // lançamento virtual de conta fixa — só atualiza localmente
    if (l._fixa) {
      setLancamentos(lancamentos.map(x => x.id===l.id ? { ...x, status:'Pago', data_lancamento: hoje } : x));
      return;
    }
    // lançamento virtual vindo de cobrancas_casos
    if (l._de_caso && l._cob_id) {
      const { data } = await supabase.from('cobrancas_casos')
        .update({ status:'Recebido', data_recebimento: hoje })
        .eq('id', l._cob_id).select().single();
      if (data) {
        setLancamentos(lancamentos.map(x => x.id===l.id ? { ...x, status:'Recebido', data_lancamento: hoje } : x));
      }
      return;
    }
    // lançamento manual no financeiro
    const { data } = await supabase.from('financeiro').update({ status: novoStatus, data_lancamento: hoje }).eq('id', l.id).select().single();
    if (data) setLancamentos(lancamentos.map(x=>x.id===data.id?data:x));
  }

  async function salvarConta() {
    if (!formConta.descricao || !formConta.valor) return;
    const item = { ...formConta, valor: Number(formConta.valor) };
    if (editandoConta) {
      const { data } = await supabase.from('contas_fixas').update(item).eq('id', editandoConta.id).select().single();
      if (data) setContasFixas(contasFixas.map(c=>c.id===editandoConta.id?data:c));
    } else {
      const { data } = await supabase.from('contas_fixas').insert([item]).select().single();
      if (data) setContasFixas([...contasFixas, data]);
    }
    setModalConta(false);
  }

  function exportarCSV() {
    const cab = ['Descrição','Tipo','Valor','Data Esperada','Data Lançamento','Projeto','Categoria','Status'];
    const lin = lancFiltrados.map(l => [`"${l.descricao||''}"`,l.tipo,Number(l.valor).toFixed(2).replace('.',','),l.data_esperada||'',l.data_lancamento||'',l.projeto||'',l.categoria||'',l.status||'']);
    const csv = [cab,...lin].map(r=>r.join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿'+csv],{type:'text/csv;charset=utf-8;'}));
    a.download='financeiro.csv'; a.click();
  }

  if (carregando) return <div style={{ padding:40, textAlign:'center', color:'#7B5EA7' }}>Carregando financeiro...</div>;

  // KPIs — fundo suave, valor em cor forte, clicável para filtrar
  const kpis = [
    { id:'entradas', label:'Entradas confirmadas', val:fmt(totalEntradas), cor:'#059669', bg:'#f0fdf4', borda:'#bbf7d0', icon:'↑' },
    { id:'saidas',   label:'Saídas pagas',         val:fmt(totalSaidas),   cor:'#dc2626', bg:'#fff5f5', borda:'#fecaca', icon:'↓' },
    { id:null,       label:'Saldo atual',           val:fmt(saldo),         cor: saldo>=0?'#059669':'#dc2626', bg: saldo>=0?'#f0fdf4':'#fff5f5', borda: saldo>=0?'#bbf7d0':'#fecaca', icon:'=' },
    { id:'pendente', label:'A receber',             val:fmt(totalPendente), cor:'#b45309', bg:'#fffbeb', borda:'#fde68a', icon:'◎' },
    { id:'atrasado', label:'Em atraso',             val:fmt(totalAtrasado), cor:'#dc2626', bg:'#fff5f5', borda:'#fca5a5', icon:'⚠' },
  ];

  const statusStyle = {
    Recebido: { bg:'#dcfce7', cor:'#16a34a' },
    Pago:     { bg:'#dcfce7', cor:'#16a34a' },
    Pendente: { bg:'#fef3c7', cor:'#b45309' },
    Atrasado: { bg:'#fee2e2', cor:'#dc2626' },
  };

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Financeiro</h2>
        <div className="section-line" />
      </div>

      {/* KPIs clicáveis */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:22 }}>
        {kpis.map((k,i) => {
          const ativo = filtroKpi === k.id && k.id !== null;
          return (
            <div key={i} onClick={() => { if (k.id) { setFiltroKpi(filtroKpi===k.id?null:k.id); setAba('lancamentos'); } }}
              style={{ background:k.bg, border:`2px solid ${ativo?k.cor:k.borda}`, borderRadius:16, padding:'16px 18px', cursor:k.id?'pointer':'default', transition:'all 0.15s', boxShadow:ativo?`0 4px 16px ${k.cor}33`:'0 1px 4px rgba(0,0,0,0.05)', transform:ativo?'translateY(-2px)':'none' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#7B5EA7', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>{k.label}</span>
                {k.id && <span style={{ fontSize:14, color:k.cor }}>{k.icon}</span>}
              </div>
              <div style={{ fontSize:20, fontWeight:800, color:k.cor, fontFamily:'Playfair Display,serif', lineHeight:1 }}>{k.val}</div>
              {k.id && <div style={{ marginTop:8, fontSize:9, color:ativo?k.cor:'#9ca3af', fontWeight:600 }}>{ativo?'▲ Filtrado — clique para limpar':'Clique para filtrar'}</div>}
            </div>
          );
        })}
      </div>

      {/* Tabs + ações */}
      <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <div className="tabs-wrap">
          <button onClick={()=>setAba('lancamentos')} className={`tab-btn${aba==='lancamentos'?' active':''}`}>Lançamentos</button>
          <button onClick={()=>setAba('fixas')}       className={`tab-btn${aba==='fixas'?      ' active':''}`}>Contas Fixas <span style={{ fontSize:10, opacity:0.8 }}>({fmt(totalFixas)}/mês)</span></button>
          <button onClick={()=>setAba('fluxo')}       className={`tab-btn${aba==='fluxo'?      ' active':''}`}>Fluxo de Caixa</button>
        </div>
        <div style={{ flex:1 }} />
        <button onClick={exportarCSV}
          style={{ padding:'8px 16px', border:'1.5px solid rgba(107,33,168,0.2)', borderRadius:8, background:'white', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
          ↓ CSV
        </button>
      </div>

      {/* ── Lançamentos ── */}
      {aba === 'lancamentos' && (
        <>
          <div className="filter-bar">
            {/* banner de filtro por KPI */}
            {filtroKpi && (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 12px', background:'#F3EBFF', borderRadius:10, fontSize:12, color:'#7B2FBE', fontWeight:700 }}>
                <span>Mostrando: {kpis.find(k=>k.id===filtroKpi)?.label}</span>
                <button onClick={()=>setFiltroKpi(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#7B2FBE', fontSize:14, lineHeight:1, padding:0 }}>×</button>
              </div>
            )}
            {!filtroKpi && <>
              <select value={filtroTipo} onChange={e=>{setFiltroTipo(e.target.value);setFiltroKpi(null);}} className={`chip-select${filtroTipo?' active':''}`}>
                <option value=''>Tipo</option>
                <option value='entrada'>Entradas</option>
                <option value='saida'>Saídas</option>
              </select>
              <select value={filtroStatus} onChange={e=>{setFiltroStatus(e.target.value);setFiltroKpi(null);}} className={`chip-select${filtroStatus?' active':''}`}>
                <option value=''>Status</option>
                <option value='Recebido'>Recebido</option>
                <option value='Pago'>Pago</option>
                <option value='Pendente'>Pendente</option>
                <option value='Atrasado'>Atrasado</option>
              </select>
              <select value={filtroProj} onChange={e=>{setFiltroProj(e.target.value);setFiltroKpi(null);}} className={`chip-select${filtroProj?' active':''}`}>
                <option value=''>Projeto</option>
                {projetos.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
              {(filtroTipo||filtroStatus||filtroProj) && (
                <button onClick={()=>{setFiltroTipo('');setFiltroStatus('');setFiltroProj('');}}
                  style={{ padding:'7px 12px', border:'none', borderRadius:10, background:'#F3EBFF', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
                  ✕ Limpar
                </button>
              )}
            </>}
            <span style={{ fontSize:12, color:'#7B5EA7', fontWeight:600 }}>{lancFiltrados.length} registro(s)</span>
            <div style={{ flex:1 }} />
            <button onClick={abrirNovoLanc}
              style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'9px 22px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)' }}>
              + Lançamento
            </button>
          </div>

          {/* Grupos por mês */}
          {(() => {
            const grupos = {};
            lancFiltrados.forEach(l => {
              const ref = l.data_lancamento || l.data_esperada || '';
              const mes = ref.slice(0,7) || 'Sem data';
              if (!grupos[mes]) grupos[mes] = [];
              grupos[mes].push(l);
            });
            const meses = Object.keys(grupos).sort((a,b)=>b.localeCompare(a));
            const nomeMes = m => {
              if (m==='Sem data') return 'Sem data';
              const d = new Date(m+'-15');
              return d.toLocaleDateString('pt-BR',{month:'long',year:'numeric'}).replace(/^\w/,c=>c.toUpperCase());
            };
            if (meses.length===0) return (
              <div className="card" style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>
                Nenhum lançamento encontrado.
              </div>
            );
            return meses.map(mes => {
              const itens = grupos[mes];
              const entMes = itens.filter(l=>l.tipo==='entrada').reduce((s,l)=>s+Number(l.valor),0);
              const saiMes = itens.filter(l=>l.tipo==='saida').reduce((s,l)=>s+Number(l.valor),0);
              return (
                <div key={mes} style={{ marginBottom:20 }}>
                  {/* cabeçalho do mês */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'#3F0051', fontFamily:'Playfair Display,serif' }}>{nomeMes(mes)}</span>
                      <span style={{ fontSize:11, color:'#16a34a', fontWeight:700 }}>+{fmt(entMes)}</span>
                      {saiMes > 0 && <span style={{ fontSize:11, color:'#dc2626', fontWeight:700 }}>−{fmt(saiMes)}</span>}
                    </div>
                    <span style={{ fontSize:10, color:'#9ca3af' }}>{itens.length} lançamento(s)</span>
                  </div>
                  {/* cards dos lançamentos */}
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {itens.map((l,i) => {
                      const ss = statusStyle[l.status] || statusStyle.Pendente;
                      const isEntrada = l.tipo==='entrada';
                      const jaLiquidado = l.status==='Recebido' || l.status==='Pago';
                      return (
                        <div key={l.id} style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:12, alignItems:'center', padding:'12px 16px', background: l._dias_atraso>0?'#fff8f8':'white', borderRadius:12, border:`1px solid ${l._dias_atraso>0?'#fca5a5':'rgba(107,33,168,0.07)'}`, borderLeft:`4px solid ${l._dias_atraso>0?'#dc2626':l._de_caso?'#7B2FBE':l._fixa?'#dc2626':isEntrada?'#059669':'#dc2626'}`, boxShadow:'0 1px 4px rgba(0,0,0,0.04)', opacity: l._fixa && jaLiquidado ? 0.6 : 1 }}>
                          <div>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3, flexWrap:'wrap' }}>
                              <span style={{ fontSize:13, fontWeight:600, color:'#1E0A3C' }}>{l.descricao}</span>
                              {l._de_caso && <span style={{ background:'#F3EBFF', color:'#7B2FBE', fontSize:9, fontWeight:700, padding:'1px 7px', borderRadius:10 }}>CASO</span>}
                              {l._fixa && <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:9, fontWeight:700, padding:'1px 7px', borderRadius:10 }}>FIXA</span>}
                              {l._dias_atraso > 0 && <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:10 }}>⚠ {l._dias_atraso} dia{l._dias_atraso>1?'s':''} em atraso</span>}
                            </div>
                            <div style={{ display:'flex', gap:10, fontSize:11, color:'#7B5EA7', flexWrap:'wrap', alignItems:'center' }}>
                              {l.projeto && l.projeto!=='Geral' && <span>🏢 {l.projeto}</span>}
                              {l.categoria && <span>🏷 {l.categoria}</span>}
                              {l.data_esperada && <span style={{ color: l._dias_atraso>0?'#dc2626':'#7B5EA7', fontWeight: l._dias_atraso>0?700:400 }}>📅 {l._dias_atraso>0?'Venceu':'Vence'} {new Date(l.data_esperada+'T00:00').toLocaleDateString('pt-BR')}</span>}
                              {l.data_lancamento && <span style={{ color:'#6b7280', fontSize:10 }}>{isEntrada ? '✓ recebido em' : '✓ pago em'} {new Date(l.data_lancamento+'T00:00').toLocaleDateString('pt-BR')}</span>}
                              <span style={{ background:ss.bg, color:ss.cor, padding:'1px 8px', borderRadius:20, fontSize:10, fontWeight:700 }}>{l.status}</span>
                            </div>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                            <div style={{ textAlign:'right' }}>
                              <div style={{ fontSize:16, fontWeight:800, color:l._de_caso?'#7B2FBE':isEntrada?'#059669':'#dc2626', fontFamily:'Playfair Display,serif' }}>
                                {isEntrada?'+':'−'}{fmt(l.valor)}
                              </div>
                              {l._de_caso && <div style={{ fontSize:9, color:'#9ca3af' }}>% nosso</div>}
                              {l._fixa && <div style={{ fontSize:9, color:'#9ca3af' }}>recorrente</div>}
                            </div>
                            <div style={{ display:'flex', gap:5 }}>
                              {!jaLiquidado && (
                                <button onClick={()=>marcarRecebidoOuPago(l)}
                                  style={{ background:l._de_caso?'#7B2FBE':isEntrada?'#059669':'#dc2626', color:'white', border:'none', borderRadius:7, padding:'6px 12px', fontSize:11, cursor:'pointer', fontWeight:700, whiteSpace:'nowrap' }}>
                                  {isEntrada ? 'A Receber' : 'A Pagar'}
                                </button>
                              )}
                              {jaLiquidado && (
                                <span style={{ fontSize:11, fontWeight:700, color:isEntrada?'#059669':'#6b7280', padding:'6px 10px' }}>
                                  {isEntrada ? '✓ Recebido' : '✓ Pago'}
                                </span>
                              )}
                              {!l._de_caso && !l._fixa && (
                                <button onClick={()=>{setEditandoLanc(l);setFormLanc({...l});setModalLanc(true);}}
                                  style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:7, padding:'6px 10px', fontSize:11, cursor:'pointer', color:'#7B5EA7', fontWeight:600 }}>
                                  ✏
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </>
      )}

      {/* ── Contas Fixas ── */}
      {aba === 'fixas' && (
        <>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <button onClick={()=>{setEditandoConta(null);setFormConta({descricao:'',valor:'',vencimento:1,categoria:'Ferramentas',ativa:true});setModalConta(true);}}
              style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'9px 22px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)' }}>
              + Conta Fixa
            </button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:16 }}>
            {contasFixas.map(c => (
              <div key={c.id} className="card" style={{ padding:20, opacity:c.ativa?1:0.5, borderLeft:`4px solid ${c.ativa?'#dc2626':'#9ca3af'}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#1E0A3C', marginBottom:3 }}>{c.descricao}</div>
                    <div style={{ fontSize:11, color:'#7B5EA7' }}>{c.categoria} · Vence dia {c.vencimento}</div>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <span style={{ background:c.ativa?'#dcfce7':'#fee2e2', color:c.ativa?'#16a34a':'#dc2626', fontSize:9, padding:'2px 7px', borderRadius:20, fontWeight:700 }}>{c.ativa?'Ativa':'Inativa'}</span>
                    <button onClick={()=>{setEditandoConta(c);setFormConta({...c});setModalConta(true);}}
                      style={{ background:'transparent', border:'1.5px solid rgba(107,33,168,0.15)', padding:'3px 8px', borderRadius:6, cursor:'pointer', fontSize:11, color:'#7B5EA7', fontWeight:600 }}>✏</button>
                  </div>
                </div>
                <div style={{ fontSize:22, fontWeight:800, color:'#dc2626', fontFamily:'Playfair Display,serif' }}>{fmt(c.valor)}<span style={{ fontSize:12, fontWeight:400, color:'#9ca3af' }}>/mês</span></div>
                <div style={{ marginTop:8, fontSize:11, color:'#7B2FBE', fontWeight:600 }}>🔄 Recorrente mensal</div>
              </div>
            ))}
            {contasFixas.length===0 && <div style={{ gridColumn:'1/-1', padding:40, textAlign:'center', color:'#9ca3af' }}>Nenhuma conta fixa cadastrada.</div>}
          </div>
          <div className="card" style={{ padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontWeight:700, fontSize:14, color:'#1E0A3C' }}>Total fixo mensal</span>
            <span style={{ fontWeight:800, fontSize:22, color:'#dc2626', fontFamily:'Playfair Display,serif' }}>{fmt(totalFixas)}</span>
          </div>
        </>
      )}

      {/* ── Fluxo de Caixa ── */}
      {aba === 'fluxo' && (() => {
        const fluxo = agruparFluxoPorMes(lancamentos, contasFixas);
        const maxVal = Math.max(...fluxo.map(m=>Math.max(m.confirmado+m.pendente,m.saidas)),1);
        const nomesMes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
        return (
          <div>
            {/* Gráfico de barras */}
            <div className="card" style={{ padding:24, marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:15, color:'#1E0A3C', marginBottom:16, fontFamily:'Playfair Display,serif' }}>Fluxo de Caixa — últimos 6 meses</div>
              <div style={{ display:'flex', gap:16, marginBottom:16 }}>
                {[{cor:'#059669',label:'Entradas confirmadas'},{cor:'#86efac',label:'A receber'},{cor:'#f87171',label:'Saídas'}].map((l,i)=>(
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:10, height:10, borderRadius:3, background:l.cor }} />
                    <span style={{ fontSize:11, color:'#7B5EA7' }}>{l.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'flex-end', height:180 }}>
                {fluxo.map((m,i) => {
                  const hC = (m.confirmado/maxVal)*160;
                  const hP = (m.pendente/maxVal)*160;
                  const hS = (m.saidas/maxVal)*160;
                  const mes = nomesMes[parseInt(m.mes.slice(5,7))-1]+'/'+m.mes.slice(2,4);
                  return (
                    <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                      <div style={{ display:'flex', gap:4, alignItems:'flex-end', height:160 }}>
                        <div style={{ width:22, display:'flex', flexDirection:'column', justifyContent:'flex-end', height:160 }}>
                          {hC>0&&<div style={{ height:hC, background:'#059669', borderRadius:'4px 4px 0 0' }} title={`Confirmado: ${fmt(m.confirmado)}`} />}
                          {hP>0&&<div style={{ height:hP, background:'#86efac', borderRadius:'4px 4px 0 0' }} title={`A receber: ${fmt(m.pendente)}`} />}
                        </div>
                        <div style={{ width:22, height:hS||2, background:'#f87171', borderRadius:'4px 4px 0 0', alignSelf:'flex-end' }} title={`Saídas: ${fmt(m.saidas)}`} />
                      </div>
                      <div style={{ fontSize:9, color:'#9ca3af', textAlign:'center', fontWeight:600 }}>{mes}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cards por mês */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
              {fluxo.slice(-3).map((m,i) => {
                const saldoMes = m.confirmado - m.saidas;
                const mesNome = new Date(m.mes+'-15').toLocaleDateString('pt-BR',{month:'long',year:'numeric'}).replace(/^\w/,c=>c.toUpperCase());
                return (
                  <div key={i} className="card" style={{ padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'#3F0051', marginBottom:14, fontFamily:'Playfair Display,serif' }}>{mesNome}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {[
                        { label:'Entradas confirmadas', val:m.confirmado, cor:'#059669' },
                        { label:'A receber (previsto)',  val:m.pendente,   cor:'#86efac' },
                        { label:'Total esperado',        val:m.confirmado+m.pendente, cor:'#16a34a', bold:true },
                        { label:'Saídas + Fixas',        val:m.saidas,     cor:'#dc2626' },
                      ].map((r,j) => (
                        <div key={j} style={{ display:'flex', justifyContent:'space-between', paddingTop:r.bold?10:0, borderTop:r.bold?'1px dashed rgba(107,33,168,0.1)':undefined }}>
                          <span style={{ fontSize:12, color:'#7B5EA7' }}>{r.label}</span>
                          <span style={{ fontSize:r.bold?14:12, fontWeight:r.bold?800:700, color:r.cor, fontFamily:r.bold?'Playfair Display,serif':undefined }}>{fmt(r.val)}</span>
                        </div>
                      ))}
                      <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, borderTop:'2px solid rgba(107,33,168,0.1)', marginTop:2 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:'#1E0A3C' }}>Saldo</span>
                        <span style={{ fontSize:17, fontWeight:800, color:saldoMes>=0?'#059669':'#dc2626', fontFamily:'Playfair Display,serif' }}>{fmt(saldoMes)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Modal lançamento ── */}
      {modalLanc && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:18, width:500, padding:32, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <h3 style={{ margin:0, color:'#3F0051', fontFamily:'Playfair Display,serif', fontSize:18 }}>{editandoLanc?'Editar':'Novo'} Lançamento</h3>
              <button onClick={()=>setModalLanc(false)} style={{ background:'transparent', border:'none', fontSize:22, cursor:'pointer', color:'#9ca3af' }}>×</button>
            </div>

            {/* Tipo */}
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:8, textTransform:'uppercase' }}>Tipo</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[{v:'entrada',l:'↑ Entrada',cor:'#059669'},{v:'saida',l:'↓ Saída',cor:'#dc2626'}].map(t=>(
                  <div key={t.v} onClick={()=>setFormLanc({...formLanc,tipo:t.v})}
                    style={{ border:`2px solid ${formLanc.tipo===t.v?t.cor:'rgba(107,33,168,0.15)'}`, borderRadius:10, padding:'10px', textAlign:'center', cursor:'pointer', background:formLanc.tipo===t.v?`${t.cor}18`:'white' }}>
                    <span style={{ fontSize:14, fontWeight:700, color:formLanc.tipo===t.v?t.cor:'#9ca3af' }}>{t.l}</span>
                  </div>
                ))}
              </div>
            </div>

            {[
              { label:'Descrição *', key:'descricao', type:'text' },
              { label:'Valor (R$) *', key:'valor', type:'number' },
              { label:'Data esperada / vencimento', key:'data_esperada', type:'date' },
              { label:'Data de recebimento/pagamento', key:'data_lancamento', type:'date' },
            ].map(f=>(
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <input type={f.type} value={formLanc[f.key]||''} onChange={e=>setFormLanc({...formLanc,[f.key]:e.target.value})} style={{ ...inp }} />
              </div>
            ))}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Categoria</label>
                <select value={formLanc.categoria} onChange={e=>setFormLanc({...formLanc,categoria:e.target.value})} style={{ ...inp }}>
                  {CATEGORIAS.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Projeto</label>
                <select value={formLanc.projeto} onChange={e=>setFormLanc({...formLanc,projeto:e.target.value})} style={{ ...inp }}>
                  {projetos.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Status</label>
              <select value={formLanc.status} onChange={e=>setFormLanc({...formLanc,status:e.target.value})} style={{ ...inp }}>
                {['Pendente','Recebido','Pago','Atrasado'].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Observações</label>
              <textarea value={formLanc.obs||''} onChange={e=>setFormLanc({...formLanc,obs:e.target.value})} rows={2}
                style={{ ...inp, resize:'vertical' }} />
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={salvarLanc}
                style={{ flex:1, background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'12px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)' }}>
                Salvar
              </button>
              <button onClick={()=>setModalLanc(false)}
                style={{ flex:1, background:'transparent', border:'1.5px solid rgba(107,33,168,0.2)', padding:'12px', borderRadius:10, cursor:'pointer', color:'#7B5EA7', fontSize:14, fontFamily:'Poppins,sans-serif', fontWeight:600 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal conta fixa ── */}
      {modalConta && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:18, width:440, padding:32, boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <h3 style={{ margin:0, color:'#3F0051', fontFamily:'Playfair Display,serif', fontSize:18 }}>{editandoConta?'Editar':'Nova'} Conta Fixa</h3>
              <button onClick={()=>setModalConta(false)} style={{ background:'transparent', border:'none', fontSize:22, cursor:'pointer', color:'#9ca3af' }}>×</button>
            </div>
            {[
              { label:'Descrição', key:'descricao', type:'text' },
              { label:'Valor mensal (R$)', key:'valor', type:'number' },
              { label:'Dia de vencimento', key:'vencimento', type:'number' },
            ].map(f=>(
              <div key={f.key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>{f.label}</label>
                <input type={f.type} value={formConta[f.key]||''} onChange={e=>setFormConta({...formConta,[f.key]:e.target.value})} style={{ ...inp }} />
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#7B5EA7', marginBottom:5, textTransform:'uppercase' }}>Categoria</label>
              <select value={formConta.categoria} onChange={e=>setFormConta({...formConta,categoria:e.target.value})} style={{ ...inp }}>
                {CATEGORIAS.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'#1E0A3C' }}>
                <input type="checkbox" checked={formConta.ativa} onChange={e=>setFormConta({...formConta,ativa:e.target.checked})} style={{ accentColor:'#7B2FBE', width:16, height:16 }} />
                Conta ativa
              </label>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={salvarConta}
                style={{ flex:1, background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'12px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)' }}>
                Salvar
              </button>
              <button onClick={()=>setModalConta(false)}
                style={{ flex:1, background:'transparent', border:'1.5px solid rgba(107,33,168,0.2)', padding:'12px', borderRadius:10, cursor:'pointer', color:'#7B5EA7', fontSize:14, fontFamily:'Poppins,sans-serif', fontWeight:600 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
